// src/components/CapturaEvidenciaEntrega.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FaCamera, FaTimes, FaCheck, FaRedo, FaBoxOpen } from 'react-icons/fa';
import './CapturaEvidenciaEntrega.css';

const CapturaEvidenciaEntrega = ({
    onComplete,
    onCancel,
    beneficiario = 'Residente',
    paqueteInfo = {}
}) => {
    const [imagenCapturada, setImagenCapturada] = useState(null);
    const [mostrandoCamara, setMostrandoCamara] = useState(false);
    const [errorCamara, setErrorCamara] = useState('');
    const [guardando, setGuardando] = useState(false);

    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const canvasRef = useRef(null);

    // Detener cámara al desmontar
    useEffect(() => {
        return () => {
            detenerCamara();
        };
    }, []);

    const detenerCamara = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setMostrandoCamara(false);
    }, []);

    const iniciarCamara = useCallback(async () => {
        setErrorCamara('');
        setMostrandoCamara(true);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });

            // Pequeña espera para asegurar que el video esté listo en el DOM
            await new Promise(resolve => setTimeout(resolve, 100));

            if (videoRef.current) {
                streamRef.current = stream;
                videoRef.current.srcObject = stream;
            }
        } catch (error) {
            console.error('Error al iniciar cámara:', error);
            setMostrandoCamara(false);
            setErrorCamara('No se pudo acceder a la cámara. Verifica los permisos.');
        }
    }, []);

    const capturarFoto = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            if (blob) {
                const url = URL.createObjectURL(blob);
                setImagenCapturada({ blob, url });
                detenerCamara();
            } else {
                setErrorCamara('Error al capturar la imagen.');
            }
        }, 'image/jpeg', 0.8);
    }, [detenerCamara]);

    const retomar = useCallback(() => {
        if (imagenCapturada?.url) {
            URL.revokeObjectURL(imagenCapturada.url);
        }
        setImagenCapturada(null);
        iniciarCamara();
    }, [imagenCapturada, iniciarCamara]);

    const handleGuardar = async () => {
        if (!imagenCapturada) return;
        setGuardando(true);
        try {
            await onComplete(imagenCapturada);
        } catch (error) {
            setErrorCamara('Error al guardar la evidencia.');
            setGuardando(false);
        }
    };

    return (
        <div className="evidencia-overlay">
            <div className="evidencia-modal" style={{ position: 'relative', overflow: 'hidden' }}>
                {/* Overlay de carga */}
                {guardando && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundColor: 'rgba(255,255,255,0.95)', zIndex: 999, display: 'flex',
                        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '24px' // Coincide con bordes del modal actual Zen (o similar)
                    }}>
                        <div style={{
                            width: '50px', height: '50px', border: '5px solid #e2e8f0',
                            borderTop: '5px solid #10b981', borderRadius: '50%',
                            animation: 'spin 1s linear infinite', marginBottom: '20px'
                        }} />
                        <h3 style={{ color: '#0f172a', marginBottom: '10px' }}>Registrando Paquete</h3>
                        <p style={{ color: '#64748b', textAlign: 'center', padding: '0 20px', fontSize: '0.95rem' }}>
                            Por favor espera un momento, estamos procesando la evidencia de seguridad...
                        </p>
                        <style>
                            {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
                        </style>
                    </div>
                )}

                <header className="evidencia-header">
                    <div className="evidencia-header-title">
                        <div className="evidencia-icon">
                            <FaBoxOpen />
                        </div>
                        <div>
                            <h2>Evidencia de Entrega</h2>
                            <p>Entregando a: <strong>{beneficiario}</strong></p>
                        </div>
                    </div>
                    <button className="evidencia-close" onClick={onCancel} disabled={guardando}>
                        <FaTimes />
                    </button>
                </header>

                <main className="evidencia-body">
                    <div className="evidencia-info-card">
                        <p>Por favor captura una imagen del residente recibiendo el paquete o del paquete entregado como evidencia de seguridad.</p>
                    </div>

                    <div className="evidencia-viewport">
                        {errorCamara && (
                            <div className="evidencia-status">
                                <p>{errorCamara}</p>
                                <button className="btn-zen" onClick={iniciarCamara}>Reintentar</button>
                            </div>
                        )}

                        {!mostrandoCamara && !imagenCapturada && !errorCamara && (
                            <div className="evidencia-placeholder" onClick={iniciarCamara}>
                                <FaCamera className="pulse-icon" />
                                <p>Presiona para iniciar cámara</p>
                            </div>
                        )}

                        {mostrandoCamara && (
                            <video ref={videoRef} autoPlay playsInline muted className="evidencia-video" />
                        )}

                        {imagenCapturada && (
                            <img src={imagenCapturada.url} alt="Evidencia" className="evidencia-preview" />
                        )}

                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                    </div>
                </main>

                <footer className="evidencia-footer">
                    {!mostrandoCamara && !imagenCapturada ? (
                        <button className="btn-zen btn-primary-zen" onClick={iniciarCamara} disabled={guardando}>
                            <FaCamera /> Abrir Cámara
                        </button>
                    ) : mostrandoCamara ? (
                        <button className="btn-zen btn-capture-zen" onClick={capturarFoto} disabled={guardando}>
                            <FaCamera /> Tomar Captura
                        </button>
                    ) : (
                        <div className="evidencia-actions-dual">
                            <button className="btn-zen btn-secondary-zen" onClick={retomar} disabled={guardando}>
                                <FaRedo /> Volver a intentar
                            </button>
                            <button className="btn-zen btn-success-zen" onClick={handleGuardar} disabled={guardando}>
                                {guardando ? 'Guardando...' : <><FaCheck /> Guardar Evidencia</>}
                            </button>
                        </div>
                    )}
                    <button className="btn-zen btn-cancel-zen" onClick={onCancel} disabled={guardando}>
                        Cancelar
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default CapturaEvidenciaEntrega;
